/**
 * EnhancedAIExplanation Component
 * Dynamic AI explanations with KPI-driven insights and visual storytelling
 * Designed for both technical staff and management audiences
 */

import { useState, useMemo } from 'react'
import {
  Brain,
  Sparkles,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Target,
  Lightbulb,
  ChevronDown,
  ChevronRight,
  Activity,
  Clock,
  BarChart3,
  GitBranch,
  Layers,
  AlertCircle,
  Info,
  ExternalLink
} from 'lucide-react'
import clsx from 'clsx'

// Types for KPI-driven explanations
interface KPIContribution {
  kpiName: string
  currentValue: number
  baselineValue: number
  unit: string
  contribution: number // -1 to 1 scale
  direction: 'positive' | 'negative' | 'neutral'
  explanation: string
  category: string
  importance: number // 0-1 scale
}

interface PredictionExplanation {
  prediction: string
  confidence: number
  timeHorizon?: string
  methodology: string
  modelType: 'foundation' | 'base' | 'per_asset'
  dataPoints: number
  lastTrained?: string
}

interface RecommendedAction {
  id: string
  action: string
  rationale: string
  expectedOutcome: string
  priority: 'critical' | 'high' | 'medium' | 'low'
  estimatedImpact: number // percentage improvement
  timeframe: string
}

interface HistoricalContext {
  pattern: string
  frequency: string
  lastOccurrence?: string
  resolution?: string
  successRate?: number
}

interface EnhancedAIExplanationProps {
  assetId: string
  healthScore: number
  previousHealthScore?: number
  anomalyScore: number
  kpiContributions: KPIContribution[]
  prediction?: PredictionExplanation
  recommendations: RecommendedAction[]
  historicalContext?: HistoricalContext
  summary: string
  detailedAnalysis?: string
  timestamp: string
  audience?: 'technician' | 'engineer' | 'manager' | 'all'
  className?: string
}

export default function EnhancedAIExplanation({
  assetId,
  healthScore,
  previousHealthScore,
  anomalyScore,
  kpiContributions,
  prediction,
  recommendations,
  historicalContext,
  summary,
  detailedAnalysis,
  timestamp,
  audience: _audience = 'all',
  className
}: EnhancedAIExplanationProps) {
  const [activeTab, setActiveTab] = useState<'summary' | 'kpis' | 'predictions' | 'actions'>('summary')
  const [expandedKPI, setExpandedKPI] = useState<string | null>(null)
  const [showDetailedAnalysis, setShowDetailedAnalysis] = useState(false)

  // Determine health status
  const healthStatus = useMemo(() => {
    if (healthScore >= 85) return { status: 'healthy', label: 'Healthy', color: 'text-status-healthy', bg: 'bg-status-healthy' }
    if (healthScore >= 60) return { status: 'warning', label: 'Warning', color: 'text-status-warning', bg: 'bg-status-warning' }
    return { status: 'critical', label: 'Critical', color: 'text-status-critical', bg: 'bg-status-critical' }
  }, [healthScore])

  // Calculate trend
  const healthTrend = useMemo(() => {
    if (!previousHealthScore) return null
    const change = healthScore - previousHealthScore
    return {
      direction: change > 0 ? 'up' : change < 0 ? 'down' : 'stable',
      value: Math.abs(change),
      isPositive: change > 0
    }
  }, [healthScore, previousHealthScore])

  // Sort contributions by importance
  const sortedContributions = useMemo(() => {
    return [...kpiContributions].sort((a, b) => b.importance - a.importance)
  }, [kpiContributions])

  // Top contributors
  const topContributors = useMemo(() => {
    const positive = sortedContributions.filter(c => c.direction === 'positive').slice(0, 3)
    const negative = sortedContributions.filter(c => c.direction === 'negative').slice(0, 3)
    return { positive, negative }
  }, [sortedContributions])

  // Urgency level
  const urgencyLevel = useMemo(() => {
    if (anomalyScore > 0.7 || healthScore < 50) return 'critical'
    if (anomalyScore > 0.4 || healthScore < 70) return 'warning'
    return 'normal'
  }, [anomalyScore, healthScore])

  const getContributionBar = (contribution: number) => {
    const absValue = Math.abs(contribution) * 100
    const isNegative = contribution < 0
    return (
      <div className="flex items-center gap-2 w-full">
        {isNegative && (
          <div className="flex-1 flex justify-end">
            <div
              className="h-2 rounded-full bg-status-critical/60"
              style={{ width: `${absValue}%` }}
            />
          </div>
        )}
        <div className="w-px h-4 bg-surface-600" />
        {!isNegative && (
          <div className="flex-1">
            <div
              className="h-2 rounded-full bg-status-healthy/60"
              style={{ width: `${absValue}%` }}
            />
          </div>
        )}
      </div>
    )
  }

  return (
    <div className={clsx(
      'rounded-xl border overflow-hidden',
      urgencyLevel === 'critical' && 'border-status-critical/30',
      urgencyLevel === 'warning' && 'border-status-warning/30',
      urgencyLevel === 'normal' && 'border-surface-700',
      className
    )}>
      {/* Header with Health Overview */}
      <div className={clsx(
        'p-4 border-b',
        urgencyLevel === 'critical' && 'bg-gradient-to-r from-status-critical/10 to-transparent border-status-critical/30',
        urgencyLevel === 'warning' && 'bg-gradient-to-r from-status-warning/10 to-transparent border-status-warning/30',
        urgencyLevel === 'normal' && 'bg-gradient-to-r from-primary-500/5 to-transparent border-surface-700'
      )}>
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className={clsx(
              'p-2 rounded-xl',
              urgencyLevel === 'critical' && 'bg-status-critical/20',
              urgencyLevel === 'warning' && 'bg-status-warning/20',
              urgencyLevel === 'normal' && 'bg-primary-500/20'
            )}>
              <Brain className={clsx(
                'w-6 h-6',
                urgencyLevel === 'critical' && 'text-status-critical',
                urgencyLevel === 'warning' && 'text-status-warning',
                urgencyLevel === 'normal' && 'text-primary-400'
              )} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-semibold text-surface-100">AI Analysis</h2>
                <span className="px-2 py-0.5 text-xs font-mono rounded bg-surface-800 text-surface-400">
                  {assetId}
                </span>
              </div>
              <div className="flex items-center gap-4 mt-1 text-xs text-surface-400">
                <span className="flex items-center gap-1">
                  <Sparkles size={12} />
                  {prediction?.modelType === 'per_asset' ? 'Per-Asset Model' : prediction?.modelType === 'foundation' ? 'Foundation Model' : 'Base Model'}
                </span>
                <span className="flex items-center gap-1">
                  <Clock size={12} />
                  {timestamp}
                </span>
              </div>
            </div>
          </div>

          {/* Health Score Badge */}
          <div className="text-right">
            <div className="flex items-center gap-2">
              <div className={clsx(
                'text-3xl font-mono font-bold',
                healthStatus.color
              )}>
                {healthScore}%
              </div>
              {healthTrend && (
                <div className={clsx(
                  'flex items-center gap-0.5 text-sm',
                  healthTrend.isPositive ? 'text-status-healthy' : 'text-status-critical'
                )}>
                  {healthTrend.direction === 'up' ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
                  {healthTrend.value.toFixed(1)}%
                </div>
              )}
            </div>
            <span className={clsx(
              'text-xs font-medium px-2 py-0.5 rounded',
              healthStatus.bg + '/20',
              healthStatus.color
            )}>
              {healthStatus.label}
            </span>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex border-b border-surface-700 bg-surface-900/50">
        {[
          { id: 'summary', label: 'Summary', icon: Lightbulb },
          { id: 'kpis', label: 'KPI Analysis', icon: BarChart3 },
          { id: 'predictions', label: 'Predictions', icon: TrendingUp },
          { id: 'actions', label: 'Actions', icon: Target }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as typeof activeTab)}
            className={clsx(
              'flex-1 py-3 px-4 flex items-center justify-center gap-2 text-sm font-medium transition-colors',
              activeTab === tab.id
                ? 'text-primary-400 border-b-2 border-primary-400 bg-primary-500/5'
                : 'text-surface-400 hover:text-surface-200 hover:bg-surface-800/50'
            )}
          >
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="p-4">
        {/* Summary Tab */}
        {activeTab === 'summary' && (
          <div className="space-y-4">
            {/* AI Summary */}
            <div className="p-4 rounded-xl bg-gradient-to-br from-primary-500/10 to-transparent border border-primary-500/20">
              <div className="flex items-start gap-3">
                <Sparkles className="text-primary-400 mt-1 flex-shrink-0" size={20} />
                <div>
                  <p className="text-surface-200 leading-relaxed">{summary}</p>
                  {detailedAnalysis && (
                    <button
                      onClick={() => setShowDetailedAnalysis(!showDetailedAnalysis)}
                      className="mt-3 flex items-center gap-1 text-sm text-primary-400 hover:text-primary-300 transition-colors"
                    >
                      {showDetailedAnalysis ? 'Hide' : 'Show'} detailed analysis
                      <ChevronDown size={14} className={clsx(showDetailedAnalysis && 'rotate-180')} />
                    </button>
                  )}
                  {showDetailedAnalysis && detailedAnalysis && (
                    <div className="mt-3 pt-3 border-t border-primary-500/20">
                      <p className="text-sm text-surface-300 leading-relaxed whitespace-pre-line">{detailedAnalysis}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Quick Insights Grid */}
            <div className="grid grid-cols-2 gap-3">
              {/* Top Positive Contributors */}
              <div className="p-3 rounded-lg bg-status-healthy/5 border border-status-healthy/20">
                <h4 className="text-xs font-semibold text-status-healthy uppercase flex items-center gap-1 mb-2">
                  <TrendingUp size={12} />
                  Positive Factors
                </h4>
                <div className="space-y-1.5">
                  {topContributors.positive.length > 0 ? (
                    topContributors.positive.map((c, i) => (
                      <div key={i} className="flex items-center justify-between text-sm">
                        <span className="text-surface-300 truncate">{c.kpiName}</span>
                        <span className="text-status-healthy font-mono">+{Math.round(c.contribution * 100)}%</span>
                      </div>
                    ))
                  ) : (
                    <span className="text-xs text-surface-500">No significant positive factors</span>
                  )}
                </div>
              </div>

              {/* Top Negative Contributors */}
              <div className="p-3 rounded-lg bg-status-critical/5 border border-status-critical/20">
                <h4 className="text-xs font-semibold text-status-critical uppercase flex items-center gap-1 mb-2">
                  <TrendingDown size={12} />
                  Negative Factors
                </h4>
                <div className="space-y-1.5">
                  {topContributors.negative.length > 0 ? (
                    topContributors.negative.map((c, i) => (
                      <div key={i} className="flex items-center justify-between text-sm">
                        <span className="text-surface-300 truncate">{c.kpiName}</span>
                        <span className="text-status-critical font-mono">{Math.round(c.contribution * 100)}%</span>
                      </div>
                    ))
                  ) : (
                    <span className="text-xs text-surface-500">No significant negative factors</span>
                  )}
                </div>
              </div>
            </div>

            {/* Historical Context */}
            {historicalContext && (
              <div className="p-3 rounded-lg bg-surface-800/50 border border-surface-700">
                <h4 className="text-xs font-semibold text-surface-400 uppercase flex items-center gap-1 mb-2">
                  <GitBranch size={12} />
                  Historical Context
                </h4>
                <p className="text-sm text-surface-300">{historicalContext.pattern}</p>
                <div className="flex items-center gap-4 mt-2 text-xs text-surface-500">
                  <span>Frequency: {historicalContext.frequency}</span>
                  {historicalContext.lastOccurrence && <span>Last: {historicalContext.lastOccurrence}</span>}
                  {historicalContext.successRate !== undefined && (
                    <span className="text-status-healthy">Success rate: {historicalContext.successRate}%</span>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* KPI Analysis Tab */}
        {activeTab === 'kpis' && (
          <div className="space-y-3">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-surface-200">KPI Contribution Analysis</h3>
              <span className="text-xs text-surface-500">{kpiContributions.length} factors analyzed</span>
            </div>

            {sortedContributions.map((kpi, index) => (
              <div
                key={kpi.kpiName}
                className={clsx(
                  'rounded-lg border overflow-hidden transition-all',
                  expandedKPI === kpi.kpiName
                    ? 'border-primary-500/30 bg-primary-500/5'
                    : 'border-surface-700 bg-surface-800/30 hover:bg-surface-800/50'
                )}
              >
                <button
                  onClick={() => setExpandedKPI(expandedKPI === kpi.kpiName ? null : kpi.kpiName)}
                  className="w-full p-3 text-left"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-surface-500 w-6">#{index + 1}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-surface-200 text-sm">{kpi.kpiName}</span>
                        <span className="text-xs text-surface-500">{kpi.category}</span>
                      </div>
                      <div className="mt-2">
                        {getContributionBar(kpi.contribution)}
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={clsx(
                        'font-mono font-semibold',
                        kpi.direction === 'positive' && 'text-status-healthy',
                        kpi.direction === 'negative' && 'text-status-critical',
                        kpi.direction === 'neutral' && 'text-surface-400'
                      )}>
                        {kpi.contribution > 0 ? '+' : ''}{Math.round(kpi.contribution * 100)}%
                      </span>
                    </div>
                    <ChevronRight
                      size={16}
                      className={clsx('text-surface-500', expandedKPI === kpi.kpiName && 'rotate-90')}
                    />
                  </div>
                </button>

                {expandedKPI === kpi.kpiName && (
                  <div className="px-3 pb-3 border-t border-surface-700/50">
                    <div className="pt-3 grid grid-cols-3 gap-4 text-center">
                      <div>
                        <span className="text-xs text-surface-500 block">Current</span>
                        <span className="font-mono text-surface-200">{kpi.currentValue} {kpi.unit}</span>
                      </div>
                      <div>
                        <span className="text-xs text-surface-500 block">Baseline</span>
                        <span className="font-mono text-surface-400">{kpi.baselineValue} {kpi.unit}</span>
                      </div>
                      <div>
                        <span className="text-xs text-surface-500 block">Importance</span>
                        <span className="font-mono text-primary-400">{Math.round(kpi.importance * 100)}%</span>
                      </div>
                    </div>
                    <p className="mt-3 text-sm text-surface-400 bg-surface-800/50 p-2 rounded">
                      {kpi.explanation}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Predictions Tab */}
        {activeTab === 'predictions' && prediction && (
          <div className="space-y-4">
            <div className="p-4 rounded-xl bg-gradient-to-br from-accent-purple/10 to-transparent border border-accent-purple/20">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-semibold text-surface-100">{prediction.prediction}</h3>
                  {prediction.timeHorizon && (
                    <span className="text-sm text-surface-400 mt-1">Time horizon: {prediction.timeHorizon}</span>
                  )}
                </div>
                <div className="text-right">
                  <div className="text-2xl font-mono font-bold text-accent-purple">
                    {Math.round(prediction.confidence * 100)}%
                  </div>
                  <span className="text-xs text-surface-500">confidence</span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 rounded-lg bg-surface-800/50 border border-surface-700">
                <span className="text-xs text-surface-500 block">Methodology</span>
                <span className="text-sm text-surface-200">{prediction.methodology}</span>
              </div>
              <div className="p-3 rounded-lg bg-surface-800/50 border border-surface-700">
                <span className="text-xs text-surface-500 block">Data Points</span>
                <span className="text-sm text-surface-200 font-mono">{prediction.dataPoints.toLocaleString()}</span>
              </div>
            </div>

            {prediction.lastTrained && (
              <div className="p-3 rounded-lg bg-surface-800/50 border border-surface-700">
                <span className="text-xs text-surface-500 block mb-1">Model Training</span>
                <span className="text-sm text-surface-200">Last trained: {prediction.lastTrained}</span>
              </div>
            )}
          </div>
        )}

        {/* Actions Tab */}
        {activeTab === 'actions' && (
          <div className="space-y-3">
            {/* Empty state when no recommendations */}
            {(!recommendations || recommendations.length === 0) && (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <div className="p-3 rounded-full bg-surface-800 mb-3">
                  <Target size={24} className="text-surface-500" />
                </div>
                <h4 className="text-sm font-medium text-surface-300 mb-1">No Actions Required</h4>
                <p className="text-xs text-surface-500 max-w-xs">
                  The system is operating within normal parameters. No immediate maintenance actions are recommended at this time.
                </p>
              </div>
            )}
            {recommendations && recommendations.map((rec) => {
              const priorityConfig = {
                critical: { bg: 'bg-status-critical/10', border: 'border-status-critical/30', text: 'text-status-critical', icon: AlertTriangle },
                high: { bg: 'bg-status-warning/10', border: 'border-status-warning/30', text: 'text-status-warning', icon: AlertCircle },
                medium: { bg: 'bg-primary-500/10', border: 'border-primary-500/30', text: 'text-primary-400', icon: Info },
                low: { bg: 'bg-surface-800', border: 'border-surface-700', text: 'text-surface-400', icon: Info }
              }[rec.priority]

              const PriorityIcon = priorityConfig.icon

              return (
                <div
                  key={rec.id}
                  className={clsx(
                    'p-4 rounded-lg border',
                    priorityConfig.bg,
                    priorityConfig.border
                  )}
                >
                  <div className="flex items-start gap-3">
                    <div className={clsx('p-2 rounded-lg', priorityConfig.bg)}>
                      <PriorityIcon size={18} className={priorityConfig.text} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className={clsx('text-xs font-bold uppercase', priorityConfig.text)}>
                          {rec.priority}
                        </span>
                        <span className="text-xs text-surface-500">{rec.timeframe}</span>
                      </div>
                      <h4 className="font-medium text-surface-100 mt-1">{rec.action}</h4>
                      <p className="text-sm text-surface-400 mt-1">{rec.rationale}</p>
                      <div className="flex items-center gap-4 mt-3 text-xs">
                        <span className="flex items-center gap-1 text-status-healthy">
                          <TrendingUp size={12} />
                          Expected: +{rec.estimatedImpact}% health
                        </span>
                        <span className="text-surface-500">{rec.expectedOutcome}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-4 py-3 bg-surface-900/50 border-t border-surface-700 flex items-center justify-between text-xs text-surface-500">
        <div className="flex items-center gap-4">
          <span className="flex items-center gap-1">
            <Layers size={12} />
            {prediction?.modelType === 'per_asset' ? 'Per-Asset' : prediction?.modelType === 'foundation' ? 'Foundation' : 'Base'} Model
          </span>
          <span className="flex items-center gap-1">
            <Activity size={12} />
            Anomaly Score: {(anomalyScore * 100).toFixed(1)}%
          </span>
        </div>
        <button className="flex items-center gap-1 text-primary-400 hover:text-primary-300 transition-colors">
          <ExternalLink size={12} />
          Export Analysis
        </button>
      </div>
    </div>
  )
}
